import React from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './PreviewOneOnOne.css';

// Format text by replacing underscores with spaces and capitalizing first letter of each word
const formatText = text => {
  if (!text) return '';

  // Replace underscores with spaces
  const textWithSpaces = text.replace(/_/g, ' ');

  // Capitalize the first letter of each word
  return textWithSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PreviewOneOnOneSchedule = ({ open, onClose, session }) => {
  // Get one_on_one session data directly if not provided
  const oneOnOneSession = session;

  if (!oneOnOneSession) return null;

  // Transform availability data if it's in API format
  const transformAvailability = () => {
    if (!oneOnOneSession.availability) return [];

    // Check if the availability is in the API format (with days array)
    const isApiFormat =
      oneOnOneSession.availability.length > 0 &&
      Array.isArray(oneOnOneSession.availability[0].days);

    if (isApiFormat) {
      // Create a map to group time slots by day
      const dayToTimeSlotsMap = {};

      // Process each availability slot
      oneOnOneSession.availability.forEach(slot => {
        slot.days.forEach(day => {
          // Initialize array for this day if it doesn't exist
          if (!dayToTimeSlotsMap[day]) {
            dayToTimeSlotsMap[day] = [];
          }

          // Add time slot to this day
          dayToTimeSlotsMap[day].push({
            start_time: slot.startTime,
            end_time: slot.endTime,
          });
        });
      });

      // Convert the map to the expected format
      const transformed = Object.entries(dayToTimeSlotsMap).map(([day, timeSlots]) => ({
        day_of_week: day,
        time_slots: timeSlots,
      }));

      return transformed;
    }

    // If the data is already in the expected format with days and time slots
    // We need to ensure time slots are properly grouped by day
    if (oneOnOneSession.availability.length > 0) {
      // Create a map of days to time slots
      const dayToTimeSlotsMap = {};

      oneOnOneSession.availability.forEach(item => {
        const day = item.day_of_week;

        if (!dayToTimeSlotsMap[day]) {
          dayToTimeSlotsMap[day] = [];
        }

        if (item.time_slots && Array.isArray(item.time_slots)) {
          dayToTimeSlotsMap[day].push(...item.time_slots);
        }
      });

      // Convert back to array format
      return Object.entries(dayToTimeSlotsMap).map(([day, timeSlots]) => ({
        day_of_week: day,
        time_slots: timeSlots,
      }));
    }

    // Return as is if already in the expected format
    return oneOnOneSession.availability;
  };

  const availability = transformAvailability();

  // Format the level of teaching
  const formattedLevel =
    oneOnOneSession.level_of_teaching === 'all'
      ? 'All Levels'
      : formatText(oneOnOneSession.level_of_teaching || oneOnOneSession.level || 'all');

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-modal-title"
      className="oneonone-preview-modal"
    >
      <Box className="oneonone-preview-modal-content">
        <Box className="oneonone-preview-modal-header">
          <Typography variant="h6" className="oneonone-preview-title">
            Preview
          </Typography>
          <IconButton onClick={onClose} className="oneonone-close-button" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="oneonone-preview-modal-body">
          <Box className="oneonone-preview-section">
            <Typography className="oneonone-section-title">Subject</Typography>
            <Box className="oneonone-section-content">
              <Typography className="oneonone-section-value">
                {oneOnOneSession.subject === 'all'
                  ? 'All'
                  : formatText(oneOnOneSession.subject || 'All')}
              </Typography>
            </Box>
          </Box>

          <Box className="oneonone-preview-section">
            <Typography className="oneonone-section-title">Level to Teach</Typography>
            <Box className="oneonone-section-content">
              <Typography className="oneonone-section-value">{formattedLevel}</Typography>
            </Box>
          </Box>

          <Box className="oneonone-preview-section">
            <Typography className="oneonone-section-title">Session Type</Typography>
            <Box className="oneonone-section-content">
              <Typography className="oneonone-text">One-On-One Sessions</Typography>
            </Box>
          </Box>

          <Box className="oneonone-preview-section">
            <Typography className="oneonone-section-title">Days & Time</Typography>
            <Box className="oneonone-section-content oneonone-days-time-section">
              {availability && availability.length > 0 ? (
                availability.map((day, index) => (
                  <Box key={index} className="oneonone-day-row">
                    <Typography className="oneonone-day-name">{day.day_of_week}</Typography>
                    <Box className="oneonone-time-slots-container">
                      {(day.time_slots || []).map((slot, slotIndex) => (
                        <Box key={slotIndex} className="oneonone-time-slot">
                          {`${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  No scheduled time slots available
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

// Helper function to format time
const formatTime = time => {
  if (!time) return '';

  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);

  let formattedHour = hour;
  let period = 'AM';

  if (hour >= 12) {
    period = 'PM';
    if (hour > 12) {
      formattedHour = hour - 12;
    }
  }

  if (hour === 0) {
    formattedHour = 12;
  }

  return `${formattedHour.toString().padStart(2, '0')}:${minutes} ${period}`;
};

export default PreviewOneOnOneSchedule;
